'use client';

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/shadcn/button';
import { projectsService } from '@/features/project/api/projects';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { useAnglesOverview } from '../hooks/use-angles-overview';
import { useProject } from '@/features/project/hooks/use-projects';
import { useConcepts } from '@/features/design/concept/hooks/use-concepts';
import { ConceptAnglesEditModal } from '@/features/design/concept/ui/ConceptAnglesEditModal';
import { findResumableDraftConcept } from '@/features/concepts-overview/lib/resolve-add-concept';

export interface CreateDesignCtaRef {
  /** Open the create-design modal prefilled with these angle ids (used when the
   *  first photos land in a previously empty project). */
  openPrefilled: (angleIds: string[]) => void;
}

interface CreateDesignCtaProps {
  projectId: string;
  isArchived?: boolean;
  /** Whether the brief text ("situation and goal") has been filled in. A design
   *  can't start on an empty brief, so the CTA is gated on it (parity across all
   *  3 platforms). */
  hasBrief?: boolean;
}

/**
 * The Brief page's single primary CTA — a centered "Create design" button that
 * sits below both Brief columns (the brief form + the building materials),
 * plus the create-design modal it opens. Lifted out of BriefMaterials so the
 * button can span the full page width and be centered under the two-column
 * layout. A design needs a settled main view, so the button only appears once
 * at least one photo passed validation and is gated while any upload/validation
 * is in flight. Mirrors the Apple/Android create-design entry points.
 */
export const CreateDesignCta = forwardRef<
  CreateDesignCtaRef,
  CreateDesignCtaProps
>(function CreateDesignCta(
  { projectId, isArchived = false, hasBrief = true },
  ref
) {
  const tConcepts = useTranslations('conceptsOverview');
  const router = useRouter();
  const { angles, hasValidAngles, hasPendingAngles } =
    useAnglesOverview(projectId);
  const queryClient = useQueryClient();
  const tErrors = useTranslations('common.errors');
  // Conversion rescue: when the only upload was auto-routed to References the
  // server flags `view_promotion` — the disabled-CTA hint then points at the
  // routed file and the "Use as a view" banner instead of asking for a photo.
  const { data: projectResponse } = useProject(projectId);
  const canPromoteView =
    projectResponse?.data?.attributes?.view_promotion?.is_available === true;
  // Lightweight concepts list (no detail/album fetch) — reuse a clean draft
  // instead of piling up empty concepts.
  const { data: concepts = [] } = useConcepts(projectId);
  const [open, setOpen] = useState(false);
  const [prefilledAngleIds, setPrefilledAngleIds] = useState<string[]>([]);
  const [isCreatingView, setIsCreatingView] = useState(false);

  // "The building does not exist yet and has no views." There is no photo to
  // wait for here, so the usual "add a photo" gate would strand the user
  // forever — the view is CONSTRUCTED on the way into the design instead.
  // Branches on the server-owned stage, never on a client guess.
  const needsConstructedView =
    projectResponse?.data?.attributes?.building_stage?.value === 'not_built' &&
    angles.length === 0;

  useImperativeHandle(
    ref,
    () => ({
      openPrefilled: (angleIds: string[]) => {
        setPrefilledAngleIds(angleIds);
        setOpen(true);
      },
    }),
    []
  );

  const handleClick = useCallback(async () => {
    const draft = findResumableDraftConcept(concepts);
    if (draft) {
      router.push(`/project/${projectId}/concept/${draft.id}`);
      return;
    }
    // A building that does not exist yet has nothing to photograph, so it
    // reaches this CTA with an EMPTY view list. Mint its constructed view
    // first (idempotent per side — a retry returns the same row) and open the
    // picker with it preselected, so the flow from here on is the ordinary one.
    if (needsConstructedView) {
      setIsCreatingView(true);
      try {
        const created = await projectsService.createConstructedView(projectId);
        await queryClient.invalidateQueries({
          queryKey: cacheKeys.projectAngles.list(projectId),
        });
        setPrefilledAngleIds(created.data?.id ? [created.data.id] : []);
        setOpen(true);
      } catch (err) {
        // Server messages are shown as-is (API-driven messages) — the 422 here
        // is "this building already exists", which is a real answer, not noise.
        toast.error(
          err instanceof ApiClientError
            ? err.getFirstError()
            : tErrors('generic')
        );
      } finally {
        setIsCreatingView(false);
      }
      return;
    }
    setPrefilledAngleIds([]);
    setOpen(true);
  }, [
    concepts,
    projectId,
    router,
    needsConstructedView,
    queryClient,
    tErrors,
  ]);

  const handleCreated = useCallback(
    (conceptId: string) => {
      router.push(`/project/${projectId}/concept/${conceptId}`);
    },
    [projectId, router]
  );

  if (isArchived) return null;

  // A design needs a settled main VIEW (a building photo or facade projection).
  // References/documents alone don't qualify, so keep the button visible but
  // disabled and explain what's missing — otherwise a user with only references
  // sees no CTA and is stuck. `hasPendingAngles` is mid-validation (an upload may
  // still resolve to a valid view), so we suppress the hint then and let the
  // disabled button read as "validating".
  // A building with no photo is NOT missing a view — its view is constructed
  // on the first click (see handleClick), so it never shows the photo hint.
  const needsView = !hasValidAngles && !needsConstructedView;
  // A design also can't start on an empty brief. Only prompt for it once a view
  // exists, so the more fundamental "add a photo" hint takes priority.
  const needsBrief = !needsView && !hasBrief;

  return (
    <>
      {/* Column hugs its content (min width) — the caller centers it in the
          pinned right-column footer. */}
      <div className="flex flex-col items-center gap-2">
        <Button
          size="lg"
          onClick={handleClick}
          disabled={
            needsView || hasPendingAngles || needsBrief || isCreatingView
          }
        >
          {tConcepts('createConcept')}
        </Button>
        {needsView && !hasPendingAngles && (
          <p className="max-w-xs text-center text-form-help text-muted-foreground">
            {canPromoteView
              ? tConcepts('viewPromotionHint')
              : tConcepts('needViewToStart')}
          </p>
        )}
        {needsBrief && !hasPendingAngles && (
          <p className="max-w-xs text-center text-form-help text-muted-foreground">
            {tConcepts('needBriefToStart')}
          </p>
        )}
      </div>

      <ConceptAnglesEditModal
        mode="create"
        projectId={projectId}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setPrefilledAngleIds([]);
        }}
        onCreated={handleCreated}
        prefilledAngleIds={prefilledAngleIds}
      />
    </>
  );
});
